import {
  confirmationCode,
  decodeBeacon,
  deriveNearbyTopic,
  derivePairKey,
  deriveTokenKey,
  encodeBeacon,
  ephemeralPubFromHex,
  fromB64Url,
  generateEphemeral,
  generateSessionToken,
  hostCommitmentMatches,
  MAX_PENDING_REQUESTS,
  readAnnounce,
  readGrant,
  readRequest,
  sealAnnounce,
  sealGrant,
  sealRequest,
  SESSION_TTL_MS,
  type EphemeralKeyPair,
  type Envelope,
} from "@psplit/ledger-core";
import { repo } from "./repo.js";
import { syncGroup } from "./relaySync.js";
import { DEFAULT_RELAY_URL } from "./config.js";
import { errorMessage } from "./errors.js";

// Drives one nearby-join session over a throwaway relay topic. The rendezvous
// always runs on this device's DEFAULT relay, not on the group's: a joiner is
// not in the group yet and has no way to know where the group's mailbox lives.
// The grant then carries the group's real relay URL for every sync after this.

const TIMEOUT_MS = 15_000;
const POLL_MS = 1500;

function rendezvousBase(topic: string): string {
  return `${DEFAULT_RELAY_URL.replace(/\/+$/, "")}/t/${topic}`;
}

export function nearbyAvailable(): boolean {
  return DEFAULT_RELAY_URL !== "";
}

async function relayFetch(url: string, init?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function post(topic: string, envelope: Envelope): Promise<void> {
  const res = await relayFetch(rendezvousBase(topic), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ e: envelope }),
  });
  if (!res.ok) throw new Error(`nearby post failed: ${res.status}`);
}

async function pull(topic: string, since: number): Promise<{ items: Envelope[]; latest: number }> {
  const res = await relayFetch(`${rendezvousBase(topic)}?since=${since}`);
  if (!res.ok) throw new Error(`nearby pull failed: ${res.status}`);
  const data = (await res.json()) as { items: Array<{ seq: number; e: Envelope }>; latest: number };
  return { items: data.items.map((i) => i.e), latest: data.latest };
}

// ---------------------------------------------------------------------------
// Host: broadcast, collect requests, approve one at a time
// ---------------------------------------------------------------------------

export interface PendingJoiner {
  eph: string;
  pub: string; // the member id a member_add would carry
  name: string;
  code: string; // the digits to check against that person's screen
  state: "waiting" | "approving" | "added" | "declined" | "error";
  error?: string;
}

export class NearbyHost {
  readonly beacon: Uint8Array;
  readonly topic: string;
  readonly startedAt = Date.now();
  pending: PendingJoiner[] = [];

  private cursor = 0;
  private announced = false;
  private readonly token: Uint8Array;
  private readonly tokenKey: Uint8Array;
  private readonly eph: EphemeralKeyPair;

  private constructor(readonly groupId: string) {
    this.token = generateSessionToken();
    this.tokenKey = deriveTokenKey(this.token);
    this.eph = generateEphemeral();
    this.beacon = encodeBeacon(this.token, this.eph.pubKey);
    this.topic = deriveNearbyTopic(this.token);
  }

  /** Fails fast for a group that has no relay: the grant would carry no usable URL. */
  static start(groupId: string): NearbyHost {
    if (!nearbyAvailable()) throw new Error("no relay configured on this device");
    const meta = repo.meta(groupId);
    if (!meta) throw new Error("unknown group");
    if (meta.relayUrl === "") throw new Error("this group syncs by QR only, so nearby cannot run");
    if (repo.membership(groupId) !== "member") throw new Error("only a member can add people");
    return new NearbyHost(groupId);
  }

  expired(): boolean {
    return Date.now() - this.startedAt > SESSION_TTL_MS;
  }

  /** Publish the host ephemeral key, then collect whatever requests have landed. */
  async poll(): Promise<void> {
    if (!this.announced) {
      await post(this.topic, await sealAnnounce(this.tokenKey, this.eph.pubKey));
      this.announced = true;
    }
    const { items, latest } = await pull(this.topic, this.cursor);
    this.cursor = latest;
    for (const envelope of items) {
      if (this.pending.length >= MAX_PENDING_REQUESTS) break;
      const req = await readRequest(this.tokenKey, envelope);
      if (req === null) continue;
      if (this.pending.some((p) => p.eph === req.eph)) continue;
      if (req.pub === repo.identity?.pubKey) continue; // our own echo
      const pairKey = derivePairKey(this.token, this.eph.privKey, ephemeralPubFromHex(req.eph));
      this.pending.push({
        eph: req.eph,
        pub: req.pub,
        name: req.name,
        code: confirmationCode(pairKey),
        state: "waiting",
      });
    }
  }

  /**
   * Approve one person: author an ordinary signed member_add, hand them the
   * group key sealed to their ephemeral key alone, then push so the rest of
   * the group learns about them.
   */
  async approve(eph: string): Promise<void> {
    const joiner = this.pending.find((p) => p.eph === eph);
    if (!joiner || joiner.state !== "waiting") return;
    joiner.state = "approving";
    const meta = repo.meta(this.groupId);
    if (!meta) throw new Error("unknown group");
    try {
      await repo.addMember(this.groupId, joiner.pub, joiner.name);
      const pairKey = derivePairKey(this.token, this.eph.privKey, ephemeralPubFromHex(eph));
      await post(
        this.topic,
        await sealGrant(pairKey, {
          groupId: this.groupId,
          groupKey: repo.groupKey(this.groupId),
          epoch: repo.epoch(this.groupId),
          relayUrl: meta.relayUrl,
          name: meta.name,
        }),
      );
      await syncGroup(this.groupId);
      joiner.state = "added";
    } catch (e) {
      joiner.state = "error";
      joiner.error = errorMessage(e);
    }
  }

  /** Declining posts nothing at all: the requester simply times out. */
  decline(eph: string): void {
    const joiner = this.pending.find((p) => p.eph === eph);
    if (joiner && joiner.state === "waiting") joiner.state = "declined";
  }
}

// ---------------------------------------------------------------------------
// Joiner: hear the beacon, ask, wait to be let in
// ---------------------------------------------------------------------------

export type JoinStage =
  | "looking-for-host"
  | "wrong-host"
  | "waiting-for-approval"
  | "joined"
  | "timed-out";

export interface JoinProgress {
  stage: JoinStage;
  code?: string;
  groupId?: string;
  groupName?: string;
  error?: string;
}

/**
 * Run the joiner half to completion. Only ever pairs with the host the beacon
 * committed to, which is what makes a poisoned grant from someone else in
 * earshot unreadable rather than merely suspicious.
 */
export async function joinNearby(
  beaconBytes: Uint8Array,
  signal: AbortSignal,
  onProgress?: (p: JoinProgress) => void,
): Promise<JoinProgress> {
  const report = (p: JoinProgress): JoinProgress => {
    onProgress?.(p);
    return p;
  };
  if (!nearbyAvailable()) {
    return report({ stage: "timed-out", error: "no relay configured on this device" });
  }
  if (!repo.identity) return report({ stage: "timed-out", error: "no identity yet" });

  const beacon = decodeBeacon(beaconBytes);
  if (beacon === null) return report({ stage: "timed-out", error: "that was not a psplit beacon" });

  const tokenKey = deriveTokenKey(beacon.token);
  const topic = deriveNearbyTopic(beacon.token);
  const mine = generateEphemeral();
  const deadline = Date.now() + SESSION_TTL_MS;

  report({ stage: "looking-for-host" });

  let cursor = 0;
  let pairKey: Uint8Array | null = null;
  let asked = false;

  while (!signal.aborted && Date.now() < deadline) {
    let batch: { items: Envelope[]; latest: number };
    try {
      batch = await pull(topic, cursor);
    } catch (e) {
      return report({ stage: "timed-out", error: errorMessage(e) });
    }
    cursor = batch.latest;

    for (const envelope of batch.items) {
      if (pairKey === null) {
        const announce = await readAnnounce(tokenKey, envelope);
        if (announce !== null) {
          const hostEph = ephemeralPubFromHex(announce.eph);
          // The beacon named a specific host. Anything else on this topic
          // claiming to be the host is someone in earshot.
          if (!hostCommitmentMatches(beacon.token, beacon.commitment, hostEph)) {
            report({ stage: "wrong-host" });
            continue;
          }
          pairKey = derivePairKey(beacon.token, mine.privKey, hostEph);
          report({ stage: "looking-for-host", code: confirmationCode(pairKey) });
        }
        continue;
      }

      const grant = await readGrant(pairKey, envelope);
      if (grant === null) continue;
      await repo.joinGroup({
        groupId: grant.groupId,
        key: fromB64Url(grant.key),
        epoch: grant.epoch,
        relayUrl: grant.relayUrl,
        name: grant.name,
      });
      await syncGroup(grant.groupId);
      return report({
        stage: "joined",
        groupId: grant.groupId,
        groupName: grant.name,
        code: confirmationCode(pairKey),
      });
    }

    if (pairKey !== null && !asked) {
      try {
        await post(
          topic,
          await sealRequest(tokenKey, mine.pubKey, repo.identity.pubKey, repo.profileName),
        );
      } catch (e) {
        return report({ stage: "timed-out", error: errorMessage(e) });
      }
      asked = true;
      report({ stage: "waiting-for-approval", code: confirmationCode(pairKey) });
    }

    await new Promise((r) => setTimeout(r, POLL_MS));
  }

  return report({
    stage: "timed-out",
    ...(pairKey === null ? {} : { code: confirmationCode(pairKey) }),
  });
}
